// Takvim kaynakları paneli (iOS Takvim tarzı) — Blazor karşılığı:
// Octo.Web.Core/Components/CalendarSourceSidebar.razor.
// Satıra tıklamak kaynağı gizler/gösterir; dış kaynaklarda elle senkron, varsayılan yerel takvim silinemez.

import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { dialogs } from '@/shared/ui/dialogStore';
import { DEFAULT_SOURCE_COLOR } from '../model/calendarModel';
import { CALENDAR_SOURCE_TYPE } from '../model/calendarTypes';
import type { CalendarSource, CalendarSourceTypeId } from '../model/calendarTypes';

export interface CalendarSourceSidebarProps {
  sources: CalendarSource[];
  onToggle: (sourceId: string) => void;
  onDelete: (sourceId: string) => void;
  onSync: (sourceId: string) => void;
  onAdd: () => void;
  /** Senkronlanan kaynak (döner ikon). */
  syncingSourceId?: string | null;
  className?: string;
}

/** Dış kaynaklar elle senkronlanabilir. */
const SYNCABLE: CalendarSourceTypeId[] = [
  CALENDAR_SOURCE_TYPE.ICalUrl,
  CALENDAR_SOURCE_TYPE.Google,
  CALENDAR_SOURCE_TYPE.Outlook,
];

function sourceTypeKey(type: CalendarSourceTypeId): string {
  switch (type) {
    case CALENDAR_SOURCE_TYPE.DataSourceObject:
      return 'calendar.sourceType.dataSource';
    case CALENDAR_SOURCE_TYPE.ICalUrl:
      return 'calendar.sourceType.ical';
    case CALENDAR_SOURCE_TYPE.Google:
      return 'calendar.sourceType.google';
    case CALENDAR_SOURCE_TYPE.Outlook:
      return 'calendar.sourceType.outlook';
    default:
      return 'calendar.sourceType.local';
  }
}

export function CalendarSourceSidebar({
  sources,
  onToggle,
  onDelete,
  onSync,
  onAdd,
  syncingSourceId,
  className,
}: CalendarSourceSidebarProps) {
  const { t } = useTranslation();

  const confirmDelete = async (source: CalendarSource) => {
    const ok = await dialogs.confirm({
      title: t('calendar.sources.delete'),
      message: t('calendar.sources.deleteConfirm', { name: source.sourceName ?? '' }),
      kind: 'delete',
    });
    if (ok) onDelete(source.calendarSourceId);
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <ul className="min-h-0 flex-1 space-y-0.5 overflow-auto p-2">
        {sources.map((source) => {
          const color = source.color || DEFAULT_SOURCE_COLOR;
          const isDefault = source.sourceType === CALENDAR_SOURCE_TYPE.Local && source.sortOrder === 0;
          const canSync = SYNCABLE.includes(source.sourceType);
          return (
            <li key={source.calendarSourceId}>
              <div className="hover:bg-bg-hover group flex items-center gap-2 rounded-lg px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => onToggle(source.calendarSourceId)}
                  aria-pressed={source.isEnabled}
                  className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
                  title={source.isEnabled ? t('calendar.sources.hide') : t('calendar.sources.show')}
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-white transition-opacity',
                      source.isEnabled ? 'opacity-100' : 'opacity-30',
                    )}
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  >
                    {source.isEnabled ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <span
                    className={cn(
                      'text-fg min-w-0 flex-1 truncate text-sm',
                      !source.isEnabled && 'text-fg-subtle line-through',
                    )}
                  >
                    {source.sourceName}
                  </span>
                  {source.sourceType === CALENDAR_SOURCE_TYPE.Local ? null : (
                    <span className="text-fg-subtle shrink-0 text-[10px] uppercase">
                      {t(sourceTypeKey(source.sourceType))}
                    </span>
                  )}
                </button>
                {canSync ? (
                  <IconButton
                    size="sm"
                    label={t('calendar.sources.sync')}
                    onClick={() => onSync(source.calendarSourceId)}
                    disabled={syncingSourceId === source.calendarSourceId}
                  >
                    <RefreshCw
                      className={cn(
                        'h-3.5 w-3.5',
                        syncingSourceId === source.calendarSourceId && 'animate-spin',
                      )}
                    />
                  </IconButton>
                ) : null}
                {isDefault ? null : (
                  <IconButton
                    size="sm"
                    label={t('calendar.sources.delete')}
                    className="hover:text-danger opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                    onClick={() => void confirmDelete(source)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                )}
              </div>
              {source.syncErrorMessage ? (
                <p className="text-warning flex items-start gap-1 px-2 pb-1 text-[11px]">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 break-words">{source.syncErrorMessage}</span>
                </p>
              ) : null}
            </li>
          );
        })}
        {sources.length === 0 ? (
          <li className="text-fg-subtle px-2 py-6 text-center text-sm">{t('calendar.sources.empty')}</li>
        ) : null}
      </ul>

      <button
        type="button"
        onClick={onAdd}
        className="text-brand-600 hover:bg-brand-500/10 focus-visible:ring-ring m-2 flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-dashed border-current/50 px-3 py-2 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        {t('calendar.sources.add')}
      </button>
    </div>
  );
}
